import { decodeStoredZip } from '../lib/storedZip'

const supportedCapabilities = new Set([
  'project-core@1',
  'static@1',
  'static-coordinate-constraints@1',
  'static-geometric-constraints@1',
  'static-node-distance-constraints@2'
])

export interface FrameLabArchiveView {
  fileName: string
  manifest: FrameLabManifest
  project: FrameLabProject
  unsupportedCapabilities: string[]
}

export interface FrameLabManifest {
  format: string
  containerFormatVersion: number
  projectID: string
  projectFormatVersion: number
  requiredCapabilities: string[]
}

export interface FrameLabProject {
  id: string
  title: string
  format?: string
  version?: number
  staticSchemaVersion?: number
  createdAt?: string
  updatedAt?: string
  canvasSettings?: FrameLabCanvasSettings
  requiredCapabilities?: string[]
  nodes: FrameLabNode[]
  elements: FrameLabElement[]
  supports: FrameLabSupport[]
  loads: FrameLabLoad[]
  loadCases?: FrameLabLoadCase[]
  combinations?: unknown[]
  dimensionConstraints?: FrameLabDimensionConstraint[]
  nodeCoordinateConstraints?: unknown[]
  nodeAlignmentConstraints?: unknown[]
  nodeDistanceConstraints?: unknown[]
  nodeAttachments?: unknown[]
  nodeElementConnections?: unknown[]
  notes?: string
  usesHighContrastDrawing?: boolean
  usesProvisionalScale?: boolean
}

export interface FrameLabCanvasSettings {
  showsGrid: boolean
  gridSpacingMM: number
  snapsToGrid: boolean
  snapsToNodes: boolean
  snapsToElements: boolean
  snapsToAlignments: boolean
  showsNodeDOFs: boolean
  showsSupportReactions: boolean
}

export interface FrameLabNode {
  id: string
  label: string
  position: { x: number; y: number }
  connection?: string
}

export interface FrameLabElement {
  id: string
  startNodeID: string
  endNodeID: string
  type: 'bar' | 'beam' | string
  label: string
}

export interface FrameLabSupport {
  id: string
  nodeID: string
  type: 'pin' | 'roller' | 'fixed' | string
  angleDegrees: number
  isEnabledInStatics?: boolean
}

export interface FrameLabLoad {
  id: string
  target: unknown
  type: 'pointForce' | 'moment' | 'distributedUniform' | string
  magnitudeN: number
  angleDegrees: number
  label: string
  caseID?: string
  isEnabled?: boolean
}

export interface FrameLabLoadCase {
  id: string
  name: string
}

export interface FrameLabDimensionConstraint {
  id: string
  elementID: string
  spanStartNodeID?: string
  spanEndNodeID?: string
  lengthMM: number
  isManual: boolean
  fixedEndpoint: 'start' | 'end'
}

export async function openFrameLabArchive(fileName: string, bytes: Uint8Array): Promise<FrameLabArchiveView> {
  const entries = decodeStoredZip(bytes)
  const manifestBytes = entries.get('manifest.json')
  const projectBytes = entries.get('project.json')
  if (!manifestBytes || !projectBytes) {
    throw new Error('Il file .framelab deve contenere manifest.json e project.json.')
  }

  const manifest = parseJSON<FrameLabManifest>(manifestBytes, 'manifest.json')
  const project = parseJSON<FrameLabProject>(projectBytes, 'project.json')
  validateManifest(manifest)
  validateProject(project, manifest)

  const capabilities = project.requiredCapabilities ?? manifest.requiredCapabilities ?? []
  const unsupportedCapabilities = capabilities.filter((capability) => !supportedCapabilities.has(capability))
  return { fileName, manifest, project, unsupportedCapabilities }
}

export function nodeLoadTarget(load: FrameLabLoad): string | undefined {
  if (typeof load.target !== 'object' || load.target === null || !('node' in load.target)) {
    return undefined
  }
  const nodeTarget = (load.target as { node?: { _0?: unknown } }).node
  return typeof nodeTarget?._0 === 'string' ? nodeTarget._0 : undefined
}

export function elementLoadTarget(load: FrameLabLoad): { elementID: string; relativePosition: number } | undefined {
  if (typeof load.target !== 'object' || load.target === null || !('element' in load.target)) {
    return undefined
  }
  const elementTarget = (load.target as { element?: { _0?: unknown; relativePosition?: unknown } }).element
  if (typeof elementTarget?._0 !== 'string') {
    return undefined
  }
  return {
    elementID: elementTarget._0,
    relativePosition: typeof elementTarget.relativePosition === 'number' ? elementTarget.relativePosition : 0.5
  }
}

function parseJSON<T>(bytes: Uint8Array, name: string): T {
  try {
    return JSON.parse(new TextDecoder().decode(bytes)) as T
  } catch {
    throw new Error(`Il file ${name} non contiene JSON valido.`)
  }
}

function validateManifest(manifest: FrameLabManifest): void {
  if (manifest.format !== 'framelab.archive' || manifest.containerFormatVersion !== 1) {
    throw new Error('Archivio .framelab non supportato: manifest non riconosciuto.')
  }
}

function validateProject(project: FrameLabProject, manifest: FrameLabManifest): void {
  if (project.id !== manifest.projectID) {
    throw new Error('Archivio .framelab non valido: identita progetto incoerente.')
  }
  if (!Array.isArray(project.nodes) || !Array.isArray(project.elements)) {
    throw new Error('Project.json non contiene geometria leggibile.')
  }
  for (const node of project.nodes) {
    if (typeof node.id !== 'string' || typeof node.position?.x !== 'number' || typeof node.position?.y !== 'number') {
      throw new Error('Project.json contiene un nodo non leggibile.')
    }
  }
}
